import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type AppView = 'welcome' | 'editor';

interface AppState {
  // Current view state
  currentView: AppView;
  setCurrentView: (view: AppView) => void;

  // New Project Dialog state
  isNewProjectDialogOpen: boolean;
  openNewProjectDialog: () => void;
  closeNewProjectDialog: () => void;

  // Theme state
  theme: 'dark' | 'light';
  toggleTheme: () => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Error state
  error: string | null;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useAppStore = create<AppState>()(
  devtools(
    (set) => ({
      // Current view
      currentView: 'welcome',
      setCurrentView: (view) => set({ currentView: view }),

      // New Project Dialog
      isNewProjectDialogOpen: false,
      openNewProjectDialog: () => set({ isNewProjectDialogOpen: true }),
      closeNewProjectDialog: () => set({ isNewProjectDialogOpen: false }),

      // Theme
      theme: (localStorage.getItem('editor-theme') as 'dark' | 'light') || 'dark',
      toggleTheme: () =>
        set((state) => {
          const newTheme = state.theme === 'dark' ? 'light' : 'dark';
          localStorage.setItem('editor-theme', newTheme);
          document.documentElement.classList.toggle('dark', newTheme === 'dark');
          return { theme: newTheme };
        }),

      // Loading
      isLoading: false,
      setIsLoading: (loading) => set({ isLoading: loading }),

      // Error
      error: null,
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'app-store',
    }
  )
);

// Re-export from other store files
export * from './projectStore';
export { useSlotAssetGeneratorStore } from './slotAssetGeneratorStore';
export { useSlotScreenGeneratorStore } from './slotScreenGeneratorStore';
